
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Product, CertificationStatus } from '@/types/portfolio';
import { toast } from 'sonner';

export const useProducts = (portfolioId: string) => {
  const queryClient = useQueryClient();
  
  const {
    data: products,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['products', portfolioId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('portfolio_id', portfolioId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Product[];
    },
    enabled: !!portfolioId
  });

  const createProduct = useMutation({
    mutationFn: async (product: { name: string; description: string | null; manufacturer?: string }) => {
      const { data, error } = await supabase
        .from('products')
        .insert({
          portfolio_id: portfolioId,
          name: product.name,
          description: product.description,
          manufacturer: product.manufacturer || null,
          certification_status: 'pending' as CertificationStatus
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as Product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', portfolioId] });
      toast.success('Product added successfully');
    },
    onError: (error: Error) => {
      toast.error(`Error adding product: ${error.message}`);
    }
  });

  const updateProduct = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: { name: string; description: string | null; manufacturer?: string } }) => {
      const { data, error } = await supabase
        .from('products')
        .update({
          name: updates.name,
          description: updates.description,
          manufacturer: updates.manufacturer || null,
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', portfolioId] });
      toast.success('Product updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Error updating product: ${error.message}`);
    }
  });

  const deleteProduct = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', portfolioId] });
      toast.success('Product removed successfully');
    },
    onError: (error: Error) => {
      toast.error(`Error removing product: ${error.message}`);
    }
  });

  return {
    products,
    isLoading,
    error,
    refetch,
    createProduct,
    updateProduct,
    deleteProduct
  };
};
